import { getSessionId } from '@fs/zion-session'
import * as Sentry from '@sentry/browser'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DOWNLOAD_FAILURE_REASONS,
  describeDownloadFailureBody,
  scrubSensitiveData,
  truncate,
} from '../util/DownloadFailureUtil'
import useNotifications from './useNotifications'

/**
 * Marker dmc-server streams ahead of a successful download so the client can tell a real file
 * apart from an error document rendered into the iframe.
 */
const SUCCESS_MARKER = '"status":200'

/**
 * Send a failed download to Sentry.
 *
 * These failures are invisible server-side for the common 401 case (`ServerExceptionHandler`
 * logs unauthorized at DEBUG), so the browser is the only place the evidence exists.
 *
 * @param {string} requestPath - The endpoint the download was POSTed to.
 * @param {string} reason - One of `DOWNLOAD_FAILURE_REASONS`.
 * @param {string} [responseBody] - The summarized failure detail already shown to the admin
 *   (see `describeDownloadFailureBody`), not the raw iframe body — kept to what's already
 *   surfaced in the UI rather than forwarding whatever an upstream error page happened to render.
 *   Still run through `scrubSensitiveData` before being attached, since the detail can echo a
 *   downstream URL (e.g. an `HttpClientErrorException` message) that may carry a session ID or
 *   other credential-shaped value.
 */
function reportDownloadFailure(requestPath, reason, responseBody) {
  Sentry.withScope((scope) => {
    scope.setLevel('warning')
    scope.setTag('download_failure_reason', reason)
    scope.setTag('download_request_path', requestPath)
    if (responseBody) {
      scope.setExtra('download_response_body', truncate(scrubSensitiveData(responseBody)))
    }
    Sentry.captureMessage(`Download failed: ${requestPath}`)
  })
}

export default function useStreamDownload() {
  const { error } = useNotifications()
  const { t } = useTranslation()

  const streamDataDownload = useCallback(
    ({ requestPath, params, bodyFields }) => {
      if (document.getElementsByName('download_iframe').length > 0) {
        document.body.removeChild(document.getElementsByName('download_iframe')[0])
      }
      if (document.getElementsByName('download_form').length > 0) {
        document.body.removeChild(document.getElementsByName('download_form')[0])
      }

      // Adding an iframe prevents the page from redirecting to the download URL
      const iframe = document.createElement('iframe')
      iframe.name = 'download_iframe'
      iframe.setAttribute('data-testid', 'download_iframe')
      iframe.style.display = 'none'

      // Submitting a form allows the same streaming functionality that a link would allow,
      // but with the ability to pass the session ID in the body instead of the URL
      document.body.appendChild(iframe)
      const form = document.createElement('form')
      form.setAttribute('data-testid', 'download_form')
      form.name = 'download_form'
      form.target = iframe.name
      form.method = 'POST'
      form.action = `${requestPath}${params ? `?${new URLSearchParams(params).toString()}` : ''}`
      form.style.display = 'none'
      const input = document.createElement('input')
      input.name = 'sessionId'
      input.value = getSessionId()
      form.appendChild(input)

      if (bodyFields) {
        Object.entries(bodyFields).forEach(([key, value]) => {
          const addInput = document.createElement('input')
          addInput.name = key
          addInput.value = value
          form.appendChild(addInput)
        })
      }

      document.body.appendChild(form)

      // `load` and `error` can both fire for one download; only the first outcome counts, and the
      // form must only be torn down once.
      let settled = false
      const settle = () => {
        if (settled) return false
        settled = true
        // `remove()` is a no-op when the form was already detached, so no parentNode guard is needed.
        form.remove()
        return true
      }

      /**
       * Report a failure once, telling the admin what the server actually said instead of the bare
       * "Error downloading file" that made these unactionable.
       */
      const fail = (reason, message, responseBody) => {
        if (!settle()) return
        error(message)
        reportDownloadFailure(requestPath, reason, responseBody)
      }

      // Error handling
      iframe.addEventListener('load', () => {
        const baseMessage = t('downloadFileError', 'Error downloading file')
        let iframeDocument
        try {
          iframeDocument = iframe.contentWindow.document
        } catch {
          // Cross-origin SecurityError — the iframe navigated off-site (typically a redirect to
          // sign-in), so the body is unreadable. Kept distinct from a server error on purpose.
          fail(
            DOWNLOAD_FAILURE_REASONS.CROSS_ORIGIN,
            t(
              'downloadFileErrorUnreadable',
              'Error downloading file: the download was redirected off-site, so the reason could not be read. Your session may have expired — reload the page and try again.'
            )
          )
          return
        }

        if (iframeDocument?.readyState !== 'complete') return

        // The iframe fires `load` for its initial about:blank before the form is submitted;
        // that is not a download outcome.
        if (iframeDocument.location?.href === 'about:blank') return

        const bodyText = iframeDocument.body?.innerText ?? ''
        if (bodyText.includes(SUCCESS_MARKER)) {
          settle()
          return
        }

        // Only the already-summarized `detail` — the same text shown in the admin-facing
        // notification below — goes to Sentry. The raw iframe body can carry more than that
        // (e.g. an upstream error page's full contents), so it is never forwarded as-is.
        const detail = describeDownloadFailureBody(bodyText)
        fail(DOWNLOAD_FAILURE_REASONS.SERVER_ERROR, detail ? `${baseMessage}: ${detail}` : baseMessage, detail)
      })

      iframe.addEventListener('error', () => {
        fail(
          DOWNLOAD_FAILURE_REASONS.NETWORK_ERROR,
          t('downloadFileErrorNetwork', 'Error downloading file: the request did not reach the server.')
        )
      })

      // Submit the form to initiate the download
      form.submit()
    },
    [error, t]
  )

  return { streamDataDownload }
}
